import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';

const CODE_LENGTH = 5;

export default function PasswordSecurityTab({ token }) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [changing, setChanging] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [notice, setNotice] = useState({ type: '', text: '' });
  const inputs = useRef([]);

  useEffect(() => {
    if (!expiresAt) return undefined;

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsRemaining(remaining);
      if (remaining === 0) {
        setExpiresAt(null);
        setCodeRequested(false);
        setNotice({
          type: 'error',
          text: 'This confirmation code has expired. Request another code and try again.'
        });
      }
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);

  const requestCode = async () => {
    setRequesting(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await axios.post(
        `${API_BASE}/account/password-change-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const lifetime = Number(response.data?.expiresInSeconds) || 300;
      setDigits(Array(CODE_LENGTH).fill(''));
      setCodeRequested(true);
      setExpiresAt(Date.now() + lifetime * 1000);
      setSecondsRemaining(lifetime);
      setNotice({
        type: 'success',
        text: response.data?.message || 'A five-digit confirmation code was sent to your email address.'
      });
      window.setTimeout(() => inputs.current[0]?.focus(), 0);
    } catch (error) {
      setNotice({
        type: 'error',
        text: error.response?.data?.message || 'Unable to send a confirmation code. Please try again.'
      });
    } finally {
      setRequesting(false);
    }
  };

  const setDigit = (index, value) => {
    const digit = String(value || '').replace(/\D/g, '').slice(-1);
    setDigits((current) => current.map((item, position) => (position === index ? digit : item)));
    if (digit && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      inputs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    pasted.split('').forEach((digit, index) => { next[index] = digit; });
    setDigits(next);
    inputs.current[Math.min(pasted.length, CODE_LENGTH) - 1]?.focus();
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();
    const code = digits.join('');
    if (!/^\d{5}$/.test(code)) {
      setNotice({ type: 'error', text: 'Enter the complete five-digit confirmation code.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setNotice({ type: 'error', text: 'The new passwords do not match.' });
      return;
    }

    setChanging(true);
    setNotice({ type: '', text: '' });
    try {
      const response = await axios.post(
        `${API_BASE}/account/change-password-with-code`,
        { code, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDigits(Array(CODE_LENGTH).fill(''));
      setNewPassword('');
      setConfirmPassword('');
      setCodeRequested(false);
      setExpiresAt(null);
      setSecondsRemaining(0);
      setNotice({ type: 'success', text: response.data?.message || 'Your password has been changed.' });
    } catch (error) {
      const errorCode = error.response?.data?.code;
      if (['code_expired', 'code_attempts_exceeded', 'code_required'].includes(errorCode)) {
        setCodeRequested(false);
        setExpiresAt(null);
        setSecondsRemaining(0);
      }
      setNotice({
        type: 'error',
        text: error.response?.data?.message || 'Unable to change your password. Please try again.'
      });
    } finally {
      setChanging(false);
    }
  };

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = String(secondsRemaining % 60).padStart(2, '0');

  return (
    <section style={styles.card} aria-labelledby="change-password-heading">
      <h2 id="change-password-heading" style={styles.heading}>Change Password</h2>
      <p style={styles.description}>
        Request a five-digit confirmation code. We will email it to the address on your account,
        and it will expire after five minutes.
      </p>

      <button type="button" onClick={requestCode} disabled={requesting} style={styles.requestButton}>
        {requesting ? 'Sending…' : codeRequested ? 'Send Another Code' : 'Email Confirmation Code'}
      </button>

      {notice.text && (
        <div role={notice.type === 'error' ? 'alert' : 'status'} style={{
          ...styles.notice,
          ...(notice.type === 'error' ? styles.errorNotice : styles.successNotice)
        }}>
          {notice.text}
        </div>
      )}

      <form onSubmit={submitPasswordChange} style={styles.form}>
        <fieldset style={styles.fieldset}>
          <legend style={styles.label}>Five-digit confirmation code</legend>
          <div style={styles.codeRow} onPaste={handlePaste}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(element) => { inputs.current[index] = element; }}
                aria-label={`Confirmation code digit ${index + 1}`}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(event) => setDigit(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                disabled={!codeRequested}
                style={styles.codeInput}
              />
            ))}
          </div>
          {codeRequested && secondsRemaining > 0 && (
            <div style={styles.timer} aria-live="polite">Code expires in {minutes}:{seconds}</div>
          )}
        </fieldset>

        <label style={styles.label}>
          New password
          <input
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            style={styles.textInput}
          />
        </label>
        <p style={styles.hint}>Use at least 8 characters, including uppercase, lowercase, a number, and a symbol.</p>

        <label style={styles.label}>
          Confirm new password
          <input
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            style={styles.textInput}
          />
        </label>

        <button
          type="submit"
          disabled={!codeRequested || changing || digits.some((digit) => !digit)}
          style={styles.submitButton}
        >
          {changing ? 'Changing Password…' : 'Change Password'}
        </button>
      </form>
    </section>
  );
}

const styles = {
  card: { background: '#fff', borderRadius: '12px', padding: 'clamp(20px, 4vw, 32px)', boxShadow: '0 2px 12px rgba(0,0,0,.08)', maxWidth: '680px' },
  heading: { margin: '0 0 8px', color: '#1f2937' },
  description: { margin: '0 0 20px', color: '#4b5563', lineHeight: 1.55 },
  requestButton: { border: 0, borderRadius: '8px', padding: '12px 18px', background: '#667eea', color: '#fff', fontWeight: 700, cursor: 'pointer' },
  notice: { marginTop: '16px', padding: '12px 14px', borderRadius: '8px', lineHeight: 1.4 },
  errorNotice: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' },
  successNotice: { background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' },
  form: { display: 'grid', gap: '18px', marginTop: '22px' },
  fieldset: { padding: 0, margin: 0, border: 0 },
  label: { display: 'grid', gap: '8px', color: '#1f2937', fontWeight: 700 },
  codeRow: { display: 'flex', flexWrap: 'nowrap', gap: 'clamp(6px, 2vw, 12px)', marginTop: '10px' },
  codeInput: { width: 'clamp(42px, 10vw, 52px)', height: '54px', boxSizing: 'border-box', border: '2px solid #d1d5db', borderRadius: '9px', textAlign: 'center', fontSize: '1.45rem', fontWeight: 700 },
  timer: { marginTop: '10px', color: '#4b5563', fontSize: '.92rem' },
  textInput: { width: '100%', boxSizing: 'border-box', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '1rem' },
  hint: { margin: '-10px 0 0', color: '#6b7280', fontSize: '.88rem' },
  submitButton: { border: 0, borderRadius: '8px', padding: '13px 18px', background: '#22a85a', color: '#fff', fontWeight: 700, cursor: 'pointer' }
};
